import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { DispatchRefusal, UpcomingEvent } from '@contracts/calendar';
import type { MeetingSummary } from '@contracts/meetings';
import {
  useCalendar,
  useCalendarWindow,
  useMeetings,
  useRecordOverride,
  useSeriesList,
} from '../api/queries';
import { Button, Empty, ListSwitch, Modal, SkeletonRows, Switch, useNotices } from '../ui';
import { useUrlParam } from '../lib/url-tab';
import { clock } from '../lib/time';
import { whyNothing } from '../meeting/why-nothing';
import { placeDay } from '../lib/week-layout';

/**
 * The calendar (PRD 67): one week, a column a day, every meeting on it — the
 * ones that happened and the ones to come — each one press from its page and
 * its series, and a switch on each one still to come.
 *
 * Aleksandr, 2026-10-08: *"мне не надо искать среди полотна кучу митингов, я
 * могу в конкретный день найти и всё"* — and *"переключалка прямо в календарном
 * виде. Удобная штука."*
 */
export function Calendar(): ReactElement {
  const [weekParam, setWeek] = useUrlParam('week');
  const monday = useMemo(() => weekOf(weekParam), [weekParam]);
  const next = addDays(monday, 7);
  const from = monday.toISOString();
  const to = next.toISOString();

  const calendar = useCalendar();
  const events = useCalendarWindow(from, to);
  const recorded = useMeetings({ from, to });
  const series = useSeriesList();
  const seriesTitle = useMemo(
    () => new Map((series.data ?? []).map((s) => [s.seriesKey, s.title])),
    [series.data],
  );

  const days = useMemo(() => {
    const items = merge(events.data ?? [], recorded.data ?? []);
    return Array.from({ length: 7 }, (_, i) => {
      const day = addDays(monday, i);
      const key = dayKey(day);
      return {
        day,
        items: items
          .filter((item) => dayKey(new Date(item.startsAt)) === key)
          .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt)),
      };
    });
  }, [events.data, recorded.data, monday]);

  const narrow = useNarrow();
  // The meeting whose details are open, by key — read from the week on every
  // render, so a switch pressed in it shows the state the server answered.
  const [openKey, setOpenKey] = useState<string | null>(null);
  const opened = openKey
    ? (days.flatMap((d) => d.items).find((item) => item.key === openKey) ?? null)
    : null;

  const thisWeek = dayKey(weekOf(null)) === dayKey(monday);
  const loading = events.isPending || recorded.isPending;
  const total = days.reduce((n, d) => n + d.items.length, 0);

  return (
    <div className="page stack-lg">
      <ListSwitch on="calendar" />
      <div className="page-head">
        <div className="stack" style={{ gap: 4 }}>
          <h1 className="t-title">{weekLabel(monday)}</h1>
          <p className="mono">
            {loading ? 'reading the week…' : `${total} ${total === 1 ? 'meeting' : 'meetings'}`}
          </p>
        </div>
        <div className="cluster" style={{ gap: 8 }}>
          <Button
            size="sm"
            className="week-step"
            aria-label="The week before"
            onClick={() => setWeek(dayKey(addDays(monday, -7)))}
          >
            ‹
          </Button>
          <Button size="sm" disabled={thisWeek} onClick={() => setWeek(null)}>
            This week
          </Button>
          <Button
            size="sm"
            className="week-step"
            aria-label="The week after"
            onClick={() => setWeek(dayKey(next))}
          >
            ›
          </Button>
        </div>
      </div>

      {calendar.data && !calendar.data.connected ? (
        <div className="band">
          <span>
            <strong>No calendar connected.</strong> Only what was recorded shows here until it is.
          </span>
          <Link to="/settings#calendar" className="btn btn-sm">
            Connect the calendar
          </Link>
        </div>
      ) : null}

      {events.isError || recorded.isError ? (
        <Empty tone="bad" lead="The week could not be read.">
          Try again in a moment.
        </Empty>
      ) : loading ? (
        <SkeletonRows rows={4} />
      ) : narrow ? (
        <WeekList days={days} seriesTitle={seriesTitle} label={weekLabel(monday)} />
      ) : (
        <WeekGrid days={days} onOpen={setOpenKey} label={weekLabel(monday)} />
      )}

      {opened ? (
        <Modal title={opened.title} onClose={() => setOpenKey(null)}>
          <Entry item={opened} seriesTitle={seriesTitle} />
        </Modal>
      ) : null}
    </div>
  );
}

type Day = { day: Date; items: Item[] };

/** One hour of the grid, in pixels. Half an hour is a two-line block. */
const HOUR_PX = 56;

/**
 * The week as a time grid (2026-10-14): hours down the side, a column a day,
 * each meeting as tall as it lasts — and meetings at the same time side by
 * side (`placeDay`). Vladimir: *"the calendar don't show when meetings are at
 * the same time or going in parallel"*. A block opens its details: the
 * meeting, its series, its state and its switch.
 */
function WeekGrid({
  days,
  onOpen,
  label,
}: {
  days: Day[];
  onOpen: (key: string) => void;
  label: string;
}): ReactElement {
  // The working day, widened to whatever the week holds.
  const hoursOf = (item: Item) => {
    const midnight = new Date(item.startsAt);
    midnight.setHours(0, 0, 0, 0);
    const from = (Date.parse(item.startsAt) - midnight.getTime()) / 3_600_000;
    return { from, to: Math.min(24, (endOf(item) - midnight.getTime()) / 3_600_000) };
  };
  const all = days.flatMap((d) => d.items.map(hoursOf));
  const first = Math.max(0, Math.floor(Math.min(8, ...all.map((h) => h.from))));
  const last = Math.min(24, Math.ceil(Math.max(19, ...all.map((h) => h.to))));
  const hours = Array.from({ length: last - first }, (_, i) => first + i);
  const today = dayKey(new Date());
  const now = new Date();
  const nowHours = now.getHours() + now.getMinutes() / 60;

  return (
    <div className="wk" aria-label={`The week of ${label}`}>
      <div className="wk-head" aria-hidden="true">
        <span />
        {days.map(({ day }) => (
          <span key={dayKey(day)} className={dayKey(day) === today ? 'today' : ''}>
            {day.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' })}
          </span>
        ))}
      </div>
      <div className="wk-body" style={{ height: (last - first) * HOUR_PX }}>
        <div className="wk-hours" aria-hidden="true">
          {hours.map((h) => (
            <span key={h} style={{ top: (h - first) * HOUR_PX }}>
              {String(h).padStart(2, '0')}:00
            </span>
          ))}
        </div>
        {days.map(({ day, items }) => (
          <section
            key={dayKey(day)}
            className={`wk-day${dayKey(day) === today ? ' today' : ''}`}
            style={{ backgroundSize: `100% ${HOUR_PX}px` }}
            aria-label={day.toLocaleDateString('en-GB', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
            })}
          >
            {placeDay(
              items.map((item) => ({
                key: item.key,
                start: Date.parse(item.startsAt),
                end: endOf(item),
                item,
              })),
            ).map(({ span, col, cols }) => {
              const h = hoursOf(span.item);
              const state = stateOf(span.item);
              const height = Math.max(22, (h.to - h.from) * HOUR_PX - 2);
              return (
                <button
                  key={span.key}
                  type="button"
                  className={`wk-block${state.tone ? ` ${state.tone}` : ' quiet'}${
                    // Half an hour or less: time and title on one line.
                    height < 40 ? ' short' : ''
                  }${
                    // Three or more side by side: the title gets the room.
                    cols >= 3 ? ' narrow' : ''
                  }`}
                  title={`${clock(span.item.startsAt)} ${span.item.title}`}
                  data-switchable={switchableOf(span.item) ? 'true' : undefined}
                  style={{
                    top: (h.from - first) * HOUR_PX,
                    height,
                    left: `calc(${(col / cols) * 100}% + 2px)`,
                    width: `calc(${100 / cols}% - 4px)`,
                  }}
                  aria-label={`${span.item.title}, ${clock(span.item.startsAt)} — ${state.label}`}
                  onClick={() => onOpen(span.key)}
                >
                  <span className="wk-block-time">{clock(span.item.startsAt)}</span>
                  <span className="wk-block-title">{span.item.title}</span>
                </button>
              );
            })}
            {dayKey(day) === today && nowHours >= first && nowHours <= last ? (
              <div className="wk-now" style={{ top: (nowHours - first) * HOUR_PX }} />
            ) : null}
          </section>
        ))}
      </div>
    </div>
  );
}

/**
 * The week on a phone: its days one under another, and the meetings that
 * overlap side by side under "at the same time" — never sideways scrolling.
 */
function WeekList({
  days,
  seriesTitle,
  label,
}: {
  days: Day[];
  seriesTitle: Map<string, string>;
  label: string;
}): ReactElement {
  const today = dayKey(new Date());
  return (
    <div className="week" aria-label={`The week of ${label}`}>
      {days.map(({ day, items }) => {
        const placed = placeDay(
          items.map((item) => ({
            key: item.key,
            start: Date.parse(item.startsAt),
            end: endOf(item),
            item,
          })),
        );
        const groups: Item[][] = [];
        for (const p of placed) (groups[p.group] ??= []).push(p.span.item);
        return (
          <section
            key={dayKey(day)}
            className={`week-day${dayKey(day) === today ? ' today' : ''}`}
            aria-label={day.toLocaleDateString('en-GB', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
            })}
          >
            <div className="week-day-head">
              <span>{day.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' })}</span>
              {dayKey(day) === today ? <span>today</span> : null}
            </div>
            <div className="week-items">
              {items.length === 0 ? (
                <span className="week-none">—</span>
              ) : (
                groups.map((group) =>
                  group.length === 1 ? (
                    <Entry key={group[0].key} item={group[0]} seriesTitle={seriesTitle} />
                  ) : (
                    <div key={group[0].key} className="week-parallel">
                      <span className="week-parallel-label">at the same time</span>
                      <div className="week-parallel-items">
                        {group.map((item) => (
                          <Entry key={item.key} item={item} seriesTitle={seriesTitle} />
                        ))}
                      </div>
                    </div>
                  ),
                )
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/** Narrow enough that the week reads as a list (the stylesheet's own breakpoint). */
function useNarrow(): boolean {
  const query = '(max-width: 860px)';
  const [narrow, setNarrow] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const change = () => setNarrow(list.matches);
    list.addEventListener('change', change);
    return () => list.removeEventListener('change', change);
  }, []);
  return narrow;
}

/** When a meeting ends: its calendar's end, else what was recorded, else half an hour. */
function endOf(item: Item): number {
  const start = Date.parse(item.startsAt);
  const end = item.endsAt ? Date.parse(item.endsAt) : NaN;
  return Number.isNaN(end) || end <= start ? start + 30 * 60_000 : end;
}

/** One meeting of the week: its time, its title, its series, its state — and its switch. */
function Entry({
  item,
  seriesTitle,
}: {
  item: Item;
  seriesTitle: Map<string, string>;
}): ReactElement {
  const override = useRecordOverride();
  const { show } = useNotices();
  const state = stateOf(item);
  const e = item.event;
  const switchable = switchableOf(item);
  const on = !!e && (e.plan === 'WILL_RECORD' || e.capture === 'SCHEDULED');

  return (
    <div className="week-item">
      <div className="week-item-top">
        <span className="mono">{clock(item.startsAt)}</span>
        {switchable && e ? (
          <Switch
            label=""
            aria-label={`Record “${item.title}”`}
            checked={on}
            disabled={override.isPending}
            onChange={(ev) => {
              const record = ev.target.checked;
              override.mutate(
                { eventId: e.id, record },
                {
                  onSuccess: () =>
                    show({
                      tone: 'success',
                      text: record
                        ? `The notetaker comes to “${item.title}”.`
                        : `No notetaker for “${item.title}” — just this one.`,
                    }),
                  onError: (error) => show({ tone: 'failure', text: error.message }),
                },
              );
            }}
          />
        ) : null}
      </div>
      <span className="week-item-title">
        {item.meetingId ? <Link to={`/m/${item.meetingId}`}>{item.title}</Link> : item.title}
      </span>
      {item.seriesKey ? (
        <Link className="week-item-series" to={`/series/${encodeURIComponent(item.seriesKey)}`}>
          {/* Its name where it says more than the meeting's own title. */}
          {seriesTitle.get(item.seriesKey) && seriesTitle.get(item.seriesKey) !== item.title
            ? seriesTitle.get(item.seriesKey)
            : 'Series'}{' '}
          ›
        </Link>
      ) : null}
      <span className={`week-item-state${state.tone ? ` ${state.tone}` : ''}`}>
        {state.dot ? <span className={`dot dot-${state.dot}`} /> : null}
        {state.label}
        {e && !e.mine && Date.parse(e.startsAt) > Date.now() ? ' · a colleague’s calendar' : ''}
      </span>
    </div>
  );
}

/**
 * Only a meeting still to come, on this person's own calendar, that a
 * notetaker could join — PRD 67 §2. Its state says why everywhere else.
 */
function switchableOf(item: Item): boolean {
  const e = item.event;
  return (
    !!e &&
    e.mine &&
    Date.parse(e.startsAt) > Date.now() &&
    (e.capture === null || e.capture === 'SCHEDULED') &&
    !NOTHING_TO_JOIN.has(e.plan as DispatchRefusal)
  );
}

/** A meeting of the week, from the calendar, the recordings, or both. */
interface Item {
  key: string;
  startsAt: string;
  /** When it ends, when anything says. */
  endsAt: string | null;
  title: string;
  meetingId: string | null;
  seriesKey: string | null;
  event: UpcomingEvent | null;
  meeting: MeetingSummary | null;
}

/**
 * One line per meeting: the calendar's entry and the recording are joined on
 * the meeting's id (PRD 44 — a calendar meeting has its row from the sync), and
 * a recording with no calendar entry — a link the notetaker was sent to, an
 * import — is a line of its own.
 */
function merge(events: UpcomingEvent[], meetings: MeetingSummary[]): Item[] {
  const items = new Map<string, Item>();
  for (const event of events) {
    const key = event.meetingId ?? event.id;
    items.set(key, {
      key,
      startsAt: event.startsAt,
      endsAt: event.endsAt,
      title: event.title,
      meetingId: event.meetingId,
      seriesKey: event.seriesKey,
      event,
      meeting: null,
    });
  }
  for (const meeting of meetings) {
    const found = items.get(meeting.id);
    if (found) {
      found.meeting = meeting;
      continue;
    }
    items.set(meeting.id, {
      key: meeting.id,
      startsAt: meeting.startedAt,
      endsAt: meeting.endedAt,
      title: meeting.title,
      meetingId: meeting.id,
      seriesKey: meeting.seriesKey,
      event: null,
      meeting,
    });
  }
  return [...items.values()];
}

/** Where nothing could join, whatever anybody says — no switch, only the reason. */
const NOTHING_TO_JOIN = new Set<DispatchRefusal>([
  'no-meeting-url',
  'unsupported-platform',
  'longer-than-a-meeting',
  'nobody-else-invited',
  'free-month-ended',
  'in-the-past',
]);

const NOTHING_WORDS: Partial<Record<DispatchRefusal, string>> = {
  'no-meeting-url': 'No link',
  'unsupported-platform': 'Not a Meet or Zoom link',
  'longer-than-a-meeting': 'Not a call',
  'nobody-else-invited': 'Just you',
  'free-month-ended': 'Free month over',
};

/** The state in a word — the same words as the lists. */
function stateOf(item: Item): { label: string; tone?: string; dot?: string } {
  const capture = item.event?.capture ?? item.meeting?.captureHealth ?? null;
  switch (capture) {
    case 'JOINING':
      return { label: 'Joining now', tone: 'green', dot: 'green' };
    case 'WAITING_ROOM':
      return { label: 'At the door — admit it', tone: 'amber', dot: 'amber' };
    case 'RECORDING':
      return { label: 'In the call', tone: 'red', dot: 'live' };
    case 'PAUSED':
      return { label: 'Paused', tone: 'amber' };
    case 'FAILED':
      return {
        label: whyNothing(item.event?.captureReason ?? item.meeting?.captureReason ?? null).pill,
        tone: 'red',
      };
    case 'DONE':
      return { label: 'Recorded', tone: 'green' };
    case 'SCHEDULED':
      if (item.event?.capture === 'SCHEDULED') {
        return { label: 'Will record', tone: 'green', dot: 'green' };
      }
      break;
    case 'UNKNOWN':
      return { label: 'Unclear — checking' };
    default:
      break;
  }
  if (Date.parse(item.startsAt) <= Date.now()) return { label: 'Not recorded' };
  const plan = item.event?.plan;
  if (!plan) return { label: '—' };
  if (plan === 'WILL_RECORD') return { label: 'Will record', tone: 'green', dot: 'green' };
  return { label: NOTHING_WORDS[plan] ?? 'Won’t record' };
}

/** Monday, 00:00 local, of the week holding `param` (a date) — this week without one. */
function weekOf(param: string | null): Date {
  const match = param ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(param) : null;
  const day = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date();
  const start = Number.isNaN(day.getTime()) ? new Date() : day;
  const monday = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  return monday;
}

function addDays(day: Date, n: number): Date {
  const next = new Date(day);
  next.setDate(next.getDate() + n);
  return next;
}

/** A local date as YYYY-MM-DD — the address's week, and a day's identity. */
function dayKey(day: Date): string {
  return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(
    day.getDate(),
  ).padStart(2, '0')}`;
}

/** "6–12 October", or "29 September – 5 October" across a month. */
function weekLabel(monday: Date): string {
  const sunday = addDays(monday, 6);
  const month = (d: Date) => d.toLocaleDateString('en-GB', { month: 'long' });
  return monday.getMonth() === sunday.getMonth()
    ? `${monday.getDate()}–${sunday.getDate()} ${month(sunday)}`
    : `${monday.getDate()} ${month(monday)} – ${sunday.getDate()} ${month(sunday)}`;
}
